import classNames from 'classnames'
import * as React from 'react'

import { TooltipInput } from '../../../../components/tooltip-input'
import { getNumberValue } from '../../utils'
import { usePortfolioBulkEditRowContext } from '../PortfolioBulkEditRowContext'
import { EditColumnProps } from './types'

const MAX_SHARES = 999999999

export function SharesEditColumn<
  StateType extends {
    shares?: number
  },
  ErrorsType extends {
    shares?: boolean
  },
>(props: EditColumnProps<StateType, ErrorsType>) {
  const { isActive, setIsInteracting } = usePortfolioBulkEditRowContext()
  const [rawValue, setRawValue] = React.useState(props.entry.shares ?? '')
  const handleBlur = React.useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      setIsInteracting(false)
      const shares = ev.currentTarget.valueAsNumber

      if (shares >= 0 && props.errors?.shares) props.setErrors((errors) => ({ ...errors, shares: false }))
      if (shares > MAX_SHARES) return
      props.setState((entry) => ({ ...entry, shares }))
    },
    [props, setIsInteracting]
  )

  const value = getNumberValue(props.entry.shares)
  const isNaNValue = isNaN(value)

  let tooltipContent = undefined
  if (props.errors?.shares && isActive) {
    if (isNaNValue) tooltipContent = 'Missing number of shares'
    if (value < 0 || typeof rawValue === 'string') tooltipContent = 'Invalid number, please enter a positive number'
  }

  const showError = props.errors?.shares && (isNaN(Number(rawValue)) || Number(rawValue) <= 0 || rawValue === '')

  React.useEffect(() => {
    const sharesValue = getNumberValue(props.entry.shares)
    if (!isNaN(sharesValue)) setRawValue(sharesValue)
  }, [props.entry.shares])

  return (
    <TooltipInput
      isMobileStyleEnabled={false}
      name="shares"
      label={props.label}
      isHorizontalLabel={props.isHorizontalLabel}
      labelClassName={props.labelClassName}
      form={props.form}
      showNumberStepper={false}
      error={showError}
      min={0}
      max={MAX_SHARES}
      maxLength={9}
      type="number"
      className={classNames(props.className, { 'is-error': props.errors?.shares })}
      inputClass={classNames(props.inputClassName, 'tabular-nums', { 'text-right': !props.inputClassName })}
      value={rawValue}
      onChange={(ev) => {
        if (props.updateOnChange !== false) {
          const numberValue = ev.currentTarget.valueAsNumber
          props.setState((entry) => ({ ...entry, shares: numberValue }))
        }
        setRawValue(ev.currentTarget.value)
      }}
      onBlur={handleBlur}
      size={props.size}
      tooltipColor="red"
      tooltipSettings={{ unstable_offset: [10, 10] }}
      tooltipContent={showError ? tooltipContent : undefined}
      isInteractive={isActive}
      onFocus={() => setIsInteracting(true)}
    />
  )
}
